import { openDB, deleteDB, IDBPDatabase } from "idb";
import { Product } from "src/shared/types";

const DB_NAME = "POS_GROCERY";
const DB_VERSION = 2;
const STORE_NAME = "products";

/** Create / Open DB */
export const dbPromise = openDB(DB_NAME, DB_VERSION, {
  upgrade(db) {
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      const store = db.createObjectStore(STORE_NAME, {
        keyPath: "_id",
      });

      // Index for barcode search
      store.createIndex("barcode", "barcodeId", { unique: false });
    }
  },
});

/** ✅ Save Products (Safe) */
export const saveProductsToIndexedDB = async (products: Product[] | null | undefined) => {
  if (!Array.isArray(products)) {
    console.warn("❌ Products is not an array:", products);
    return;
  }

  const db = await dbPromise;
  const tx = db.transaction(STORE_NAME, "readwrite");
  const store = tx.objectStore(STORE_NAME);

  for (const product of products) {
    let id: any = product?._id;

    // Handle Mongo ObjectId { $oid: "..." }
    if (typeof id === "object" && id?.$oid) {
      id = id.$oid;
    }

    // Convert to string
    if (id) {
      id = String(id);
    }

    // Skip invalid
    if (!id) {
      console.warn("⚠️ Skipping invalid product:", product);
      continue;
    }

    const formattedProduct = {
      ...product,
      _id: id,
    };

    await store.put(formattedProduct);
  }

  await tx.done;
};

/** ✅ Get All Products */
export const getProductsFromIndexedDB = async (): Promise<Product[]> => {
  const db = await dbPromise;
  return db.getAll(STORE_NAME);
};

/** ✅ Get Product by Barcode */
export const getProductByBarcode = async (barcode: string) => {
  const db = await dbPromise;
  const tx = db.transaction(STORE_NAME, "readonly");
  const index = tx.objectStore(STORE_NAME).index("barcode");

  const result = await index.get(barcode);
  return result;
};

/** ✅ Delete Single Product */
export const deleteProductFromIndexedDB = async (id: string) => {
  const db = await dbPromise;
  const tx = db.transaction(STORE_NAME, "readwrite");
  const store = tx.objectStore(STORE_NAME);

  await store.delete(id);

  await tx.done;
};

/** ✅ Clear All Products (Recommended reset) */
export const clearProductsFromIndexedDB = async () => {
  const db = await dbPromise;
  const tx = db.transaction(STORE_NAME, "readwrite");

  await tx.objectStore(STORE_NAME).clear();

  await tx.done;
};

/** ❌ Delete Entire DB (Hard Reset) */
export const deleteIndexedDB = async () => {
  await deleteDB(DB_NAME);
};

/** 🚀 Reset + Save (Best Practice) */
export const resetAndSaveProducts = async (products: Product[] | null | undefined) => {
  await clearProductsFromIndexedDB();
  await saveProductsToIndexedDB(products);
};
